import React from "react";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Box, Tooltip, Typography } from "@mui/material";
import { LOCATION_STATUS_CONST } from "@/config/constants";
import { getFormatDate } from "@/helpers/utils";

type StyleObject = Record<string, unknown> & {
  backgroundColor?: string;
};

type LocationStatusNoteLocation = {
  location_status?: number | string | null;
  reopening_date?: string | null;
};

type InlineStatusNoteProps = {
  text?: React.ReactNode;
  tooltipText?: React.ReactNode;
  icon?: React.ReactNode;
  maxWidth?: number;
  noteSx?: StyleObject;
  tooltipSx?: StyleObject;
};

type TemporaryClosedStatusNoteProps = Omit<
  InlineStatusNoteProps,
  "text" | "tooltipText"
> & {
  location?: LocationStatusNoteLocation;
};

export const isTemporarilyClosedLocation = (
  location: LocationStatusNoteLocation = {}
) =>
  Number(location?.location_status) ===
  LOCATION_STATUS_CONST.TEMPORARILY_CLOSED.ID;

const getTemporaryClosedMessage = (
  location: LocationStatusNoteLocation = {}
) => {
  const reopeningDate = location?.reopening_date
    ? getFormatDate(location.reopening_date)
    : "";

  if (!reopeningDate) {
    return "This location is temporarily closed. If you have questions about this status, please contact your customer success manager.";
  }

  return `This location is temporarily closed and is expected to reopen on ${reopeningDate}. If you have questions about this status, please contact your customer success manager.`;
};

const getTemporaryClosedText = (location: LocationStatusNoteLocation = {}) => {
  const reopeningDate = location?.reopening_date
    ? getFormatDate(location.reopening_date, "M/D/YYYY")
    : "";

  if (!reopeningDate) {
    return "Temporarily closed";
  }

  return `Temporarily closed, reopens on ${reopeningDate}.`;
};

const InlineStatusNote = ({
  text,
  tooltipText,
  icon = <ErrorOutlineRoundedIcon />,
  maxWidth = 390,
  noteSx = {},
  tooltipSx = {},
}: InlineStatusNoteProps) => {
  if (!text) return null;

  const note = (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "5px",
        maxWidth: "100%",
        minHeight: 20,
        borderRadius: "var(--radius-round)",
        backgroundColor: "var(--status-warning-tint)",
        color: "var(--status-warning)",
        px: "8px",
        py: "3px",
        fontSize: 12,
        fontWeight: 500,
        lineHeight: "18px",
        letterSpacing: 0,
        whiteSpace: "normal",
        wordBreak: "break-word",
        ...noteSx,
      }}
    >
      {icon ? (
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexShrink: 0,
            "& svg": {
              width: 16,
              height: 16,
              color: "currentColor",
            },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Box component="span">{text}</Box>
    </Box>
  );

  if (!tooltipText) return note;

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Typography
          component="span"
          sx={{
            display: "block",
            color: "inherit",
            fontSize: 12,
            fontWeight: 500,
            lineHeight: "18px",
            letterSpacing: 0,
          }}
        >
          {tooltipText}
        </Typography>
      }
      slotProps={{
        popper: {
          sx: {
            zIndex: 99999,
          },
        },
        tooltip: {
          sx: {
            maxWidth,
            borderRadius: "var(--radius-container)",
            backgroundColor: "var(--primary-base)",
            color: "var(--neutral-white)",
            px: "8px",
            py: "7px",
            boxShadow: "var(--shadow-floating)",
            ...tooltipSx,
          },
        },
        arrow: {
          sx: {
            color: tooltipSx.backgroundColor || "var(--primary-base)",
          },
        },
      }}
    >
      {note}
    </Tooltip>
  );
};

export const TemporaryClosedStatusNote = ({
  location,
  ...props
}: TemporaryClosedStatusNoteProps) => {
  if (!isTemporarilyClosedLocation(location)) return null;

  const text = getTemporaryClosedText(location);
  const message = getTemporaryClosedMessage(location);

  return <InlineStatusNote text={text} tooltipText={message} {...props} />;
};

export default InlineStatusNote;
